/** Documents (/): list with search and project filter, create dialog; opens pages at /doc/:slug */

import { useEffect, useId, useState, type FormEvent } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { FileText, Plus, Search } from 'lucide-react'
import type { AccessLevel } from '@agentwiki/shared'
import { useCreateDocument, useDocuments } from '../hooks/use-documents'
import { useProjects } from '../hooks/use-projects'
import { useDebounce } from '../hooks/use-debounce'
import { useCurrentProjectSlug } from '../hooks/use-current-project'
import { useAppStore } from '../stores/app-store'
import { useT } from '../i18n'
import { cn } from '../lib/utils'
import { Modal } from '../components/ui/modal'
import { AccessLevelBadge, ACCESS_LEVEL_LABEL_KEYS } from '../components/documents/access-level-badge'
import { buttonPrimaryClass, buttonSecondaryClass, cardClass, errorTextClass, hintClass, inputClass, labelClass } from '../components/ui/styles'

const PAGE_SIZE = 50
/** `public` means published at /pub/:slug, so new documents start private or shared; publishing stays an explicit action */
const CREATE_ACCESS_LEVELS: AccessLevel[] = ['private', 'specific']
const ALL_PROJECTS = 'all'

export function DocumentsPage() {
  const t = useT()
  const locale = useAppStore((s) => s.locale)
  const currentProject = useCurrentProjectSlug()
  const [params, setParams] = useSearchParams()
  const { data: projects } = useProjects()

  const project = params.get('project') ?? currentProject
  const offset = Math.max(0, Number(params.get('offset')) || 0)
  const [query, setQuery] = useState(params.get('q') ?? '')
  const search = useDebounce(query.trim(), 300)
  const creating = params.get('new') === '1'

  const updateParams = (next: Record<string, string | null>) => {
    setParams(
      (prev) => {
        const merged = new URLSearchParams(prev)
        for (const [key, value] of Object.entries(next)) {
          if (value === null || value === '') merged.delete(key)
          else merged.set(key, value)
        }
        return merged
      },
      { replace: true },
    )
  }

  // Keep the search term in the URL (a new term starts from the first page); only the debounced term triggers this
  useEffect(() => {
    if ((params.get('q') ?? '') !== search) updateParams({ q: search, offset: null })
  }, [search])

  const { data, isLoading, isError, refetch } = useDocuments({
    project: project === ALL_PROJECTS ? undefined : project,
    search: search || undefined,
    limit: PAGE_SIZE,
    offset,
  })

  const docs = data?.data ?? []
  const total = data?.total ?? 0
  const dateFormat = new Intl.DateTimeFormat(locale, { dateStyle: 'medium' })
  const projectName = (id: string | null) => projects?.find((p) => p.id === id)?.name

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 px-4 py-6 md:px-8 md:py-8">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <h1 className="font-display text-2xl font-semibold tracking-tight text-fg">{t('documents.title')}</h1>
          <p className="mt-1 text-sm text-fg-muted">{t('documents.subtitle')}</p>
        </div>
        <button type="button" onClick={() => updateParams({ new: '1' })} className={buttonPrimaryClass}>
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('documents.new')}
        </button>
      </header>

      <div className="flex flex-col gap-2 sm:flex-row">
        <label className="relative min-w-0 flex-1">
          <span className="sr-only">{t('documents.search')}</span>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-subtle" aria-hidden="true" />
          <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('documents.search')} className={cn(inputClass, 'pl-9')} />
        </label>
        <label className="sm:w-56">
          <span className="sr-only">{t('documents.projectFilter')}</span>
          <select value={project} onChange={(e) => updateParams({ project: e.target.value, offset: null })} className={inputClass}>
            <option value={ALL_PROJECTS}>{t('documents.allProjects')}</option>
            {projects?.map((p) => (
              <option key={p.id} value={p.slug}>
                {p.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {isLoading ? (
        <p className="py-10 text-center text-sm text-fg-subtle">{t('common.loading')}</p>
      ) : isError ? (
        <div className={errorTextClass} role="alert">
          {t('documents.loadError')}{' '}
          <button type="button" onClick={() => void refetch()} className="cursor-pointer font-medium underline underline-offset-2">
            {t('common.retry')}
          </button>
        </div>
      ) : docs.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border-strong py-14 text-center text-fg-subtle">
          <FileText className="h-8 w-8" aria-hidden="true" />
          <p className="text-sm">{search ? t('documents.noMatch') : t('documents.empty')}</p>
        </div>
      ) : (
        <>
          <ul className={cn(cardClass, 'divide-y divide-border')}>
            {docs.map((doc) => (
              <li key={doc.id}>
                <Link to={`/doc/${encodeURIComponent(doc.slug)}`} className="flex flex-col gap-1 px-4 py-3 hover:bg-surface-hover sm:flex-row sm:items-center sm:gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-fg">{doc.title}</p>
                    {(doc.description || doc.summary) && <p className="mt-0.5 line-clamp-1 text-sm text-fg-muted">{doc.description || doc.summary}</p>}
                  </div>
                  <div className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg-subtle">
                    {project === ALL_PROJECTS && projectName(doc.projectId) && <span>{projectName(doc.projectId)}</span>}
                    <AccessLevelBadge level={doc.accessLevel} />
                    <span>{dateFormat.format(new Date(doc.updatedAt))}</span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
          {total > PAGE_SIZE && (
            <nav aria-label={t('documents.pagination')} className="flex items-center justify-between gap-2 text-sm text-fg-muted">
              <span>{t('documents.range', { from: offset + 1, to: Math.min(offset + PAGE_SIZE, total), total })}</span>
              <div className="flex gap-2">
                <button
                  type="button"
                  disabled={offset === 0}
                  onClick={() => updateParams({ offset: String(Math.max(0, offset - PAGE_SIZE)) })}
                  className={buttonSecondaryClass}
                >
                  {t('documents.previous')}
                </button>
                <button
                  type="button"
                  disabled={offset + PAGE_SIZE >= total}
                  onClick={() => updateParams({ offset: String(offset + PAGE_SIZE) })}
                  className={buttonSecondaryClass}
                >
                  {t('documents.next')}
                </button>
              </div>
            </nav>
          )}
        </>
      )}

      {creating && (
        <CreateDocumentDialog defaultProject={project === ALL_PROJECTS ? currentProject : project} onClose={() => updateParams({ new: null })} />
      )}
    </div>
  )
}

function CreateDocumentDialog({ defaultProject, onClose }: { defaultProject: string; onClose: () => void }) {
  const t = useT()
  const navigate = useNavigate()
  const { data: projects } = useProjects()
  const createDocument = useCreateDocument()
  const id = useId()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [project, setProject] = useState(defaultProject)
  const [accessLevel, setAccessLevel] = useState<AccessLevel>('private')
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!title.trim() || !description.trim()) {
      setError(t('documents.create.required'))
      return
    }
    setError(null)
    try {
      const doc = await createDocument.mutateAsync({ title: title.trim(), description: description.trim(), project, accessLevel })
      navigate(`/doc/${encodeURIComponent(doc.slug)}?edit=1`)
    } catch (err) {
      setError(t('documents.create.failed', { message: err instanceof Error ? err.message : String(err) }))
    }
  }

  return (
    <Modal title={t('documents.create.title')} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div className="space-y-1.5">
          <label htmlFor={`${id}-title`} className={labelClass}>
            {t('documents.create.docTitle')}
          </label>
          <input id={`${id}-title`} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={500} required className={inputClass} />
        </div>
        <div className="space-y-1.5">
          <label htmlFor={`${id}-description`} className={labelClass}>
            {t('documents.create.description')}
          </label>
          <textarea
            id={`${id}-description`}
            aria-describedby={`${id}-description-hint`}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={1000}
            rows={3}
            required
            className={cn(inputClass, 'resize-y')}
          />
          <p id={`${id}-description-hint`} className={hintClass}>
            {t('documents.create.descriptionHint')}
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <label htmlFor={`${id}-project`} className={labelClass}>
              {t('nav.project')}
            </label>
            <select id={`${id}-project`} value={project} onChange={(e) => setProject(e.target.value)} className={inputClass}>
              {projects?.map((p) => (
                <option key={p.id} value={p.slug}>
                  {p.name}
                </option>
              )) ?? <option value={project}>{project}</option>}
            </select>
          </div>
          <div className="space-y-1.5">
            <label htmlFor={`${id}-access`} className={labelClass}>
              {t('documents.accessLevel')}
            </label>
            <select id={`${id}-access`} value={accessLevel} onChange={(e) => setAccessLevel(e.target.value as AccessLevel)} className={inputClass}>
              {CREATE_ACCESS_LEVELS.map((level) => (
                <option key={level} value={level}>
                  {t(ACCESS_LEVEL_LABEL_KEYS[level])}
                </option>
              ))}
            </select>
          </div>
        </div>
        {error && (
          <p role="alert" className={errorTextClass}>
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className={buttonSecondaryClass}>
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={createDocument.isPending} className={buttonPrimaryClass}>
            {createDocument.isPending ? t('documents.create.submitting') : t('documents.create.submit')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
